import { useState, useEffect } from 'react';
import { Storage } from 'aws-amplify';

// In-memory cache for signed S3 URLs. Persists across component mounts
// for the lifetime of the page session. Signed URLs are valid for ~15 min
// by default, so this avoids redundant Storage.get calls for the same image.
const avatarCache = new Map<string, string>();

/**
 * Resolves an S3 imageName to a signed URL with in-memory caching.
 * Returns the cached URL immediately if available, otherwise fetches async.
 */
export function useAvatar(imageName?: string | null): string | null {
  const [url, setUrl] = useState<string | null>(() => {
    if (!imageName) return null;
    return avatarCache.get(imageName) || null;
  });

  useEffect(() => {
    if (!imageName) {
      setUrl(null);
      return;
    }

    // Return cached immediately
    const cached = avatarCache.get(imageName);
    if (cached) {
      setUrl(cached);
      return;
    }

    let mounted = true;

    Storage.get(imageName)
      .then((signedUrl) => {
        if (!mounted) return;
        const resolved = signedUrl as string;
        avatarCache.set(imageName, resolved);
        setUrl(resolved);
      })
      .catch(() => {
        if (mounted) setUrl(null);
      });

    return () => {
      mounted = false;
    };
  }, [imageName]);

  return url;
}
